import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import LabelsSection from './LabelsSection';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://blixo-backend-production.up.railway.app/api' });
API.interceptors.request.use(cfg => {
  const token = localStorage.getItem('blixo_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const CATEGORIES = ['all', 'general', 'important', 'follow-up'];
const PRIORITIES  = ['all', 'low', 'medium', 'high', 'urgent'];

const PRIORITY_COLORS = {
  low:    '#00ffb4',
  medium: '#00d4ff',
  high:   '#ffd700',
  urgent: '#ff4444',
};

const CATEGORY_COLORS = {
  general:    'rgba(255,255,255,0.3)',
  important:  '#ff4444',
  'follow-up':'#ffd700',
};

export default function NotesPanel({ user }) {
  const [notes,        setNotes]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [showForm,     setShowForm]     = useState(false);
  const [search,       setSearch]       = useState('');
  const [filterCat,    setFilterCat]    = useState('all');
  const [filterPri,    setFilterPri]    = useState('all');
  const [filterPinned, setFilterPinned] = useState(false);
  const [filterArch,   setFilterArch]   = useState(false);

  // Form state
  const [title,    setTitle]    = useState('');
  const [content,  setContent]  = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [saving,   setSaving]   = useState(false);

  const loadNotes = useCallback(async () => {
    if (!user?._id) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/notes/${user._id}`);
      setNotes(data);
    } catch (err) {
      console.error('Load notes error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?._id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const { data } = await API.post('/notes', {
        userId: user._id, title, content, category, priority,
      });
      setNotes(prev => [data, ...prev]);
      setTitle(''); setContent(''); setCategory('general'); setPriority('medium');
      setShowForm(false);
    } catch (err) {
      console.error('Save note error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePin = async (note) => {
    try {
      const { data } = await API.put(`/notes/${note._id}`, { isPinned: !note.isPinned });
      setNotes(prev => prev.map(n => n._id === data._id ? data : n)
        .sort((a, b) => b.isPinned - a.isPinned));
    } catch (err) { console.error(err); }
  };

  const handleArchive = async (note) => {
    try {
      const { data } = await API.put(`/notes/${note._id}`, { isArchived: !note.isArchived });
      setNotes(prev => prev.map(n => n._id === data._id ? data : n));
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await API.delete(`/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n._id !== noteId));
    } catch (err) { console.error(err); }
  };

  const filtered = notes.filter(n => {
    if (filterCat !== 'all' && n.category !== filterCat) return false;
    if (filterPri !== 'all' && n.priority !== filterPri) return false;
    if (filterPinned && !n.isPinned) return false;
    if (filterArch && !n.isArchived) return false;
    if (!filterArch && n.isArchived) return false;
    if (search && !n.title.toLowerCase().includes(search.toLowerCase()) &&
        !n.content.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (!user) return (
    <div style={s.empty}>
      <div style={s.emptyIcon}>📝</div>
      <div style={s.emptyText}>Select a user to view notes</div>
    </div>
  );

  return (
    <div style={s.panel}>
      {/* User details */}
      <div style={s.userCard}>
        <div style={s.userCardTitle}>User Details</div>
        <div style={s.userField}><span style={s.fieldLabel}>Full Name</span><span style={s.fieldValue}>{user.name}</span></div>
        <div style={s.userField}><span style={s.fieldLabel}>User ID</span><span style={{ ...s.fieldValue, fontSize: '10px', color: 'rgba(255,255,255,0.3)', wordBreak: 'break-all' }}>{user._id}</span></div>
        <div style={s.userField}><span style={s.fieldLabel}>Email</span><span style={s.fieldValue}>{user.email}</span></div>
        <div style={s.userField}><span style={s.fieldLabel}>Status</span><span style={{ ...s.fieldValue, color: user.isVip ? '#ffd700' : user.isBlocked ? '#ff4444' : '#00ffb4' }}>{user.isVip ? '⭐ VIP' : user.isBlocked ? '🚫 Blocked' : '✅ Active'}</span></div>
      </div>

      <LabelsSection user={user} />
      {/* Notes section */}
      <div style={s.notesSection}>
        {/* Header */}
        <div style={s.notesHeader}>
          <span style={s.notesTitle}>User Notes</span>
          <button style={showForm ? s.cancelBtn : s.addBtn} onClick={() => setShowForm(!showForm)}>
            {showForm ? 'CANCEL' : '+ ADD NOTE'}
          </button>
        </div>

        {/* Add note form */}
        {showForm && (
          <div style={s.form}>
            <input
              style={s.formInput}
              placeholder="Note Title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={100}
            />
            <textarea
              style={{ ...s.formInput, height: '90px', resize: 'vertical' }}
              placeholder="Note Content"
              value={content}
              onChange={e => setContent(e.target.value)}
              maxLength={1000}
            />
            <div style={s.formRow}>
              <select style={s.formSelect} value={category} onChange={e => setCategory(e.target.value)}>
                <option value="general">General</option>
                <option value="important">Important</option>
                <option value="follow-up">Follow Up</option>
              </select>
              <select style={s.formSelect} value={priority} onChange={e => setPriority(e.target.value)}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <button
              style={{ ...s.saveBtn, opacity: saving || !title.trim() ? 0.5 : 1 }}
              onClick={handleSave}
              disabled={saving || !title.trim()}
            >
              {saving ? 'SAVING...' : 'SAVE NOTE'}
            </button>
          </div>
        )}

        {/* Search */}
        <input
          style={s.search}
          placeholder="🔍  Search notes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {/* Filters */}
        <div style={s.filterRow}>
          <select style={s.filterSelect} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            {CATEGORIES.map(c => (
              <option key={c} value={c}>{c === 'all' ? 'All Categories' : c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
          <select style={s.filterSelect} value={filterPri} onChange={e => setFilterPri(e.target.value)}>
            {PRIORITIES.map(p => (
              <option key={p} value={p}>{p === 'all' ? 'All Priorities' : p.charAt(0).toUpperCase() + p.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Pinned / Archived toggles */}
        <div style={s.toggleRow}>
          <label style={s.toggleLabel}>
            <input type="checkbox" checked={filterPinned} onChange={e => setFilterPinned(e.target.checked)} style={{ marginRight: '5px' }} />
            Pinned
          </label>
          <label style={s.toggleLabel}>
            <input type="checkbox" checked={filterArch} onChange={e => setFilterArch(e.target.checked)} style={{ marginRight: '5px' }} />
            Archived
          </label>
        </div>

        {/* Notes list */}
        <div style={s.notesList}>
          {loading && <div style={s.emptyText}>Loading...</div>}
          {!loading && filtered.length === 0 && (
            <div style={s.emptyText}>No notes for this user.</div>
          )}
          {filtered.map(note => (
            <div key={note._id} style={{
              ...s.noteCard,
              opacity: note.isArchived ? 0.5 : 1,
              borderLeft: `3px solid ${PRIORITY_COLORS[note.priority] || '#00ffb4'}`,
            }}>
              <div style={s.noteTop}>
                <span style={s.noteTitle}>{note.isPinned ? '📌 ' : ''}{note.title}</span>
                <div style={s.noteActions}>
                  <button style={s.noteBtn} title={note.isPinned ? 'Unpin' : 'Pin'} onClick={() => handlePin(note)}>
                    {note.isPinned ? '📌' : '📍'}
                  </button>
                  <button style={s.noteBtn} title={note.isArchived ? 'Unarchive' : 'Archive'} onClick={() => handleArchive(note)}>
                    🗃️
                  </button>
                  <button style={{ ...s.noteBtn, color: '#ff4444' }} title="Delete" onClick={() => handleDelete(note._id)}>
                    🗑️
                  </button>
                </div>
              </div>
              {note.content && <div style={s.noteContent}>{note.content}</div>}
              <div style={s.noteMeta}>
                <span style={{ color: CATEGORY_COLORS[note.category] || 'rgba(255,255,255,0.3)' }}>
                  {note.category.toUpperCase()}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.2)' }}> — </span>
                <span style={{ color: PRIORITY_COLORS[note.priority] }}>
                  {note.priority.toUpperCase()}
                </span>
                <span style={s.noteDate}>
                  {new Date(note.createdAt).toLocaleDateString('en-GB')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const s = {
  panel:        { width: '280px', minWidth: '280px', height: '100%', overflowY: 'auto', background: '#0d1420', borderLeft: '1px solid rgba(0,255,180,0.1)', display: 'flex', flexDirection: 'column', fontFamily: "'Exo 2',sans-serif" },
  empty:        { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '40px 20px' },
  emptyIcon:    { fontSize: '36px' },
  emptyText:    { color: 'rgba(255,255,255,0.2)', fontSize: '13px', textAlign: 'center' },

  userCard:     { padding: '16px', borderBottom: '1px solid rgba(0,255,180,0.08)', flexShrink: 0 },
  userCardTitle:{ color: '#e2e8f0', fontSize: '13px', fontWeight: '700', marginBottom: '12px', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px' },
  userField:    { marginBottom: '8px' },
  fieldLabel:   { display: 'block', color: 'rgba(255,255,255,0.3)', fontSize: '10px', letterSpacing: '1px', marginBottom: '2px' },
  fieldValue:   { display: 'block', color: '#e2e8f0', fontSize: '13px', fontWeight: '600' },

  notesSection: { flex: 1, display: 'flex', flexDirection: 'column', padding: '14px', gap: '10px', overflowY: 'auto' },
  notesHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  notesTitle:   { color: '#e2e8f0', fontSize: '13px', fontWeight: '700', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px' },
  addBtn:       { background: 'transparent', border: '1.5px solid #7b61ff', borderRadius: '8px', color: '#7b61ff', fontSize: '11px', fontWeight: '700', padding: '6px 12px', cursor: 'pointer', letterSpacing: '1px' },
  cancelBtn:    { background: 'transparent', border: '1.5px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontWeight: '700', padding: '6px 12px', cursor: 'pointer', letterSpacing: '1px' },

  form:         { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(0,255,180,0.1)', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 },
  formInput:    { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '10px 12px', color: '#fff', fontSize: '13px', fontFamily: "'Exo 2',sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' },
  formRow:      { display: 'flex', gap: '8px' },
  formSelect:   { flex: 1, background: '#1a2535', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 10px', color: '#e2e8f0', fontSize: '12px', outline: 'none', cursor: 'pointer' },
  saveBtn:      { background: 'linear-gradient(90deg,#ffd700,#ffaa00)', border: 'none', borderRadius: '10px', padding: '12px', color: '#080c14', fontSize: '13px', fontWeight: '900', cursor: 'pointer', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '2px' },

  search:       { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px 12px', color: '#fff', fontSize: '13px', fontFamily: "'Exo 2',sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box', flexShrink: 0 },
  filterRow:    { display: 'flex', gap: '8px', flexShrink: 0 },
  filterSelect: { flex: 1, background: '#1a2535', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 6px', color: '#e2e8f0', fontSize: '11px', outline: 'none', cursor: 'pointer' },
  toggleRow:    { display: 'flex', gap: '16px', flexShrink: 0 },
  toggleLabel:  { color: 'rgba(255,255,255,0.35)', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center' },

  notesList:    { display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' },
  noteCard:     { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '12px 14px', cursor: 'default' },
  noteTop:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' },
  noteTitle:    { color: '#e2e8f0', fontSize: '13px', fontWeight: '700', flex: 1, marginRight: '8px' },
  noteActions:  { display: 'flex', gap: '4px', flexShrink: 0 },
  noteBtn:      { background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: '2px', opacity: 0.6 },
  noteContent:  { color: 'rgba(255,255,255,0.45)', fontSize: '12px', lineHeight: '1.5', marginBottom: '8px' },
  noteMeta:     { display: 'flex', alignItems: 'center', fontSize: '10px', letterSpacing: '0.5px' },
  noteDate:     { marginLeft: 'auto', color: 'rgba(255,255,255,0.2)', fontSize: '10px' },
};
